/**
 * Profile Utilities
 *
 * Helper functions for profile-related operations, including
 * child profile detection for privacy protection.
 */

/**
 * Age ranges that indicate a child profile (under 18)
 * Based on the age_range field in profiles table
 *
 * NOTE: These must match the age range options in profile forms
 */
const CHILD_AGE_RANGES = ['Under 12', '12-18'];

/**
 * Checks if a profile belongs to a child (under 18 years old)
 * Used for privacy protections like disabling session recording
 *
 * @param profile - Profile object with optional ageRange field (Convex schema uses camelCase)
 * @returns true if profile is identified as a child, false otherwise
 */
export function isChildProfile(profile?: { ageRange?: string | null }): boolean {
  if (!profile?.ageRange) {
    // If ageRange is not set, assume adult for backwards compatibility
    return false;
  }

  return CHILD_AGE_RANGES.includes(profile.ageRange);
}

/**
 * Checks if session recording should be disabled for a profile
 * Combines user consent with child profile protection
 *
 * @param userConsent - Whether user has consented to session recording
 * @param profile - Profile object with optional ageRange field (Convex schema uses camelCase)
 * @returns true if recording should be disabled
 */
export function shouldDisableSessionRecording(
  userConsent: boolean,
  profile?: { ageRange?: string | null },
): boolean {
  // Disable if user hasn't consented OR profile is a child
  return !userConsent || isChildProfile(profile);
}
